import { useMemo, FC } from 'react';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Textarea,
  Text,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { ApiKeyTemplate } from 'types/apiKeyTemplate';
import { CrawlerRegistrationFormFields } from '../../../types/crawlerRegistrationFormFields';

type Props = {
  values: CrawlerRegistrationFormFields;
  handleBack: () => void;
  handleSubmit: () => void;
  selectedApiKeyTemplate: ApiKeyTemplate | null;
};

export const CrawlerRegistrationConfirmModalView: FC<Props> = ({
  values,
  handleBack,
  handleSubmit,
  selectedApiKeyTemplate,
}) => {
  const apiKeyTemplateValue = useMemo(
    () =>
      values.apiKeyTemplateId === '' ? '不要' : selectedApiKeyTemplate?.name,
    [selectedApiKeyTemplate?.name, values.apiKeyTemplateId],
  );

  return (
    <form onSubmit={handleSubmit}>
      <ModalContent maxHeight="90vh">
        <ModalHeader p="40px">クローラーの登録</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" px="40px">
          <Stack spacing="6" direction="column">
            <FormControl>
              <FormLabel>クローラーID</FormLabel>
              <Input
                type="text"
                value={values.id}
                width="240px"
                variant="flushed"
                isReadOnly
                isTruncated
              />
            </FormControl>
            <FormControl>
              <FormLabel>クローラー名</FormLabel>
              <Input
                type="text"
                value={values.name}
                variant="flushed"
                isReadOnly
                isTruncated
              />
            </FormControl>
            <FormControl>
              <FormLabel>イメージ名</FormLabel>
              <Input
                type="text"
                value={values.imageName}
                variant="flushed"
                isReadOnly
                isTruncated
              />
            </FormControl>
            <FormControl>
              <FormLabel>APIキーテンプレート</FormLabel>
              <Input
                type="text"
                value={apiKeyTemplateValue}
                variant="flushed"
                isReadOnly
                isTruncated
              />
            </FormControl>
            <FormControl>
              <FormLabel>バージョン</FormLabel>
              <Input
                type="text"
                value={values.version}
                width="240px"
                variant="flushed"
                isReadOnly
                isTruncated
              />
            </FormControl>
            <FormControl>
              <FormLabel>説明</FormLabel>
              <Textarea
                value={values.description}
                rows={3}
                variant="flushed"
                isReadOnly
              />
            </FormControl>
            <FormControl>
              <FormLabel>リリース日</FormLabel>
              <Input
                type="text"
                value={
                  values.releasedAt instanceof Date
                    ? format(values.releasedAt, 'yyyy-MM-dd')
                    : ''
                }
                width="240px"
                variant="flushed"
                isReadOnly
              />
            </FormControl>
            <FormControl>
              <FormLabel>手動実行対応</FormLabel>
              <CheckboxGroup value={values.adhocSupportedIntervals}>
                <Stack spacing={8} direction="row">
                  <Checkbox value="hourly" isReadOnly pointerEvents="none">
                    hourly
                  </Checkbox>
                  <Checkbox value="daily" isReadOnly pointerEvents="none">
                    daily
                  </Checkbox>
                  <Checkbox value="weekly" isReadOnly pointerEvents="none">
                    weekly
                  </Checkbox>
                  <Checkbox value="monthly" isReadOnly pointerEvents="none">
                    monthly
                  </Checkbox>
                  <Checkbox value="none" isReadOnly pointerEvents="none">
                    手動実行
                  </Checkbox>
                </Stack>
              </CheckboxGroup>
            </FormControl>
            <FormControl>
              <FormLabel>UI表示テンプレート</FormLabel>
              <Textarea
                value={values.configTemplate}
                rows={8}
                variant="flushed"
                isReadOnly
              />
            </FormControl>
            <FormControl>
              <FormLabel>リリースチャンネル</FormLabel>
              <CheckboxGroup value={values.releasedChannel}>
                <Stack spacing={8} direction="row">
                  <Checkbox value="stable" isReadOnly pointerEvents="none">
                    安定版
                  </Checkbox>
                  <Checkbox value="beta" isReadOnly pointerEvents="none">
                    ベータ版
                  </Checkbox>
                </Stack>
              </CheckboxGroup>
            </FormControl>
          </Stack>
          <Text align="center" fontWeight="bold" mt="60px">
            上記の内容でクローラーを登録しますか？
          </Text>
        </ModalBody>
        <ModalFooter justifyContent="center" mx="20px" gap="16px" p="40px">
          <Button
            w="140px"
            variant="outline"
            colorScheme="blackAlpha"
            onClick={handleBack}
          >
            入力画面に戻る
          </Button>
          <Button w="140px" colorScheme="blackAlpha" type="submit">
            登録
          </Button>
        </ModalFooter>
      </ModalContent>
    </form>
  );
};
